import type { Tool } from "../types/document.ts";
import type { Action } from "../state/actions.ts";
import spritesheetUrl from "../assets/toolbar-spritesheet.png";

interface Props {
  activeTool: Tool;
  activeColor: string;
  activeWidth: number;
  activeFill: string | undefined;
  undoEnabled: boolean;
  redoEnabled: boolean;
  dispatch: (action: Action) => void;
}

const ICON_DISPLAY = 28;
const COL_W = 208;
const ROW_H = 191;
const SHEET_W = 624;
const SHEET_H = 1716;
const SCALE = ICON_DISPLAY / ROW_H;
const BG_W = SHEET_W * SCALE;
const BG_H = SHEET_H * SCALE;

const spritePos = (col: number, row: number) => ({
  backgroundImage: `url(${spritesheetUrl})`,
  backgroundSize: `${BG_W}px ${BG_H}px`,
  backgroundPosition: `${-(col * COL_W * SCALE)}px ${-(row * ROW_H * SCALE)}px`,
  backgroundRepeat: "no-repeat" as const,
  width: `${ICON_DISPLAY}px`,
  height: `${ICON_DISPLAY}px`,
  imageRendering: "auto" as const,
});

const TOOLS: { tool: Tool; label: string; key: string; col: number; row: number }[] = [
  { tool: "text", label: "Text", key: "T", col: 1, row: 3 },
  { tool: "pencil", label: "Pencil", key: "P", col: 0, row: 2 },
  { tool: "line", label: "Line", key: "L", col: 0, row: 4 },
  { tool: "rect", label: "Rect", key: "R", col: 0, row: 5 },
  { tool: "ellipse", label: "Ellipse", key: "E", col: 0, row: 6 },
  { tool: "emoji", label: "Emoji", key: "M", col: 0, row: 0 },
];

export function Toolbar({
  activeTool,
  activeColor,
  activeWidth,
  activeFill,
  undoEnabled,
  redoEnabled,
  dispatch,
}: Props) {
  const showFill = activeTool === "rect" || activeTool === "ellipse";

  return (
    <div
      class="flex items-center gap-3 px-4 py-2 rounded-xl shadow-sm flex-wrap"
      style={{ background: "#F5E6B8", border: "1.5px solid #D4B078" }}
    >
      <div class="flex gap-1">
        {TOOLS.map(({ tool, label, key, col, row }) => (
          <button
            key={tool}
            class="flex flex-col items-center gap-0.5 rounded-lg px-2 py-1 transition-colors"
            style={
              activeTool === tool
                ? { background: "#C48A3F", color: "#FFF8E7" }
                : { background: "#FFF3D6", color: "#5D4E37" }
            }
            onClick={() => dispatch({ type: "SET_TOOL", tool })}
            title={`${label} (${key})`}
          >
            <div style={spritePos(col, row)} />
            <span class="text-xs leading-none">{label}</span>
          </button>
        ))}
      </div>

      <div class="h-8" style={{ width: "1px", background: "#D4B078" }} />

      <div class="flex items-center gap-2">
        <input
          type="color"
          value={activeColor}
          onInput={(e) =>
            dispatch({ type: "SET_COLOR", color: (e.target as HTMLInputElement).value })
          }
          class="w-7 h-7 border-0 rounded cursor-pointer"
          title="Stroke color"
        />
        <input
          type="range"
          min={1}
          max={12}
          value={activeWidth}
          onInput={(e) =>
            dispatch({ type: "SET_WIDTH", width: Number((e.target as HTMLInputElement).value) })
          }
          class="w-20"
          title={`Width: ${activeWidth}`}
          style={{ accentColor: "#C48A3F" }}
        />
        <span class="text-xs w-4" style={{ color: "#8B7355" }}>
          {activeWidth}
        </span>
      </div>

      {showFill && (
        <>
          <div class="h-8" style={{ width: "1px", background: "#D4B078" }} />
          <div class="flex items-center gap-1">
            <label class="flex items-center gap-1 text-xs" style={{ color: "#5D4E37" }}>
              <input
                type="checkbox"
                checked={activeFill !== undefined}
                onChange={(e) =>
                  dispatch({
                    type: "SET_FILL",
                    fill: (e.target as HTMLInputElement).checked ? activeColor : undefined,
                  })
                }
                style={{ accentColor: "#C48A3F" }}
              />
              Fill
            </label>
            {activeFill !== undefined && (
              <input
                type="color"
                value={activeFill}
                onInput={(e) =>
                  dispatch({ type: "SET_FILL", fill: (e.target as HTMLInputElement).value })
                }
                class="w-6 h-6 border-0 rounded cursor-pointer"
              />
            )}
          </div>
        </>
      )}

      <div class="h-8" style={{ width: "1px", background: "#D4B078" }} />

      <div class="flex gap-1">
        <button
          class="px-2 py-1 text-sm rounded-lg transition-colors disabled:opacity-30 disabled:cursor-default"
          style={{ background: "#FFF3D6", color: "#5D4E37" }}
          disabled={!undoEnabled}
          onClick={() => dispatch({ type: "UNDO" })}
          title="Undo (Ctrl+Z)"
        >
          Undo
        </button>
        <button
          class="px-2 py-1 text-sm rounded-lg transition-colors disabled:opacity-30 disabled:cursor-default"
          style={{ background: "#FFF3D6", color: "#5D4E37" }}
          disabled={!redoEnabled}
          onClick={() => dispatch({ type: "REDO" })}
          title="Redo (Ctrl+Shift+Z)"
        >
          Redo
        </button>
      </div>
    </div>
  );
}
